/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import type { IntegrationSettings } from '../../../ts/integration';

/**
 * Returns true if all values in the object or array are null, undefined,
 * or empty strings.
 *
 * @param obj - An object or an array to verify.
 * @returns True if all keys are null, undefined, or empty strings.
 **/
export const areFieldsEmpty = obj => {
  let isEmpty = true;
  _.forEach(obj, value => {
    const isValueEmpty =
      typeof value === 'object' ? areFieldsEmpty(value) : _.isEmpty(value);
    if (isValueEmpty === false) {
      isEmpty = false;
      return false;
    }
  });

  return isEmpty;
};

export const objectDifference = (
  a: Object | null,
  b: Object | null
): IntegrationSettings => {
  if (_.isNil(a)) {
    return b;
  }
  if (_.isNil(b)) {
    return a;
  }
  const unionedKeys = _.union(Object.keys(a), Object.keys(b));

  const difference = {};

  for (const key of unionedKeys) {
    if (_.isNil(a[key]) || _.isNil(b[key]) || typeof a[key] !== typeof b[key]) {
      difference[key] = a[key] ?? b[key];
    } else if (typeof a[key] === 'object' && typeof b[key] === 'object') {
      const childObject = objectDifference(a[key], b[key]);
      if (!_.isEmpty(childObject)) {
        difference[key] = childObject;
      }
    } else {
      if (a[key] !== b[key]) {
        difference[key] = a[key];
      }
    }
  }

  return difference;
};

export const objectSimilarity = (a: Object, b: Object): Object => {
  const intersectionKeys = _.intersection(Object.keys(a), Object.keys(b));

  const intersection = {};

  for (const key of intersectionKeys) {
    if (typeof a[key] === 'object' && typeof b[key] === 'object') {
      const childObject = objectSimilarity(a[key], b[key]);
      if (!_.isEmpty(childObject)) {
        intersection[key] = childObject;
      }
    } else if (typeof a[key] === typeof b[key] && a[key] === b[key]) {
      intersection[key] = a[key];
    }
  }

  return intersection;
};

export const filterPrivateFields = (settings: IntegrationSettings): Object => {
  const removeEmptyStrings = (source: Object): Object =>
    Object.keys(source).reduce((finalObject, key) => {
      if (typeof source[key] === 'object') {
        const filteredSource = removeEmptyStrings(source[key]);
        if (!_.isEmpty(filteredSource)) {
          finalObject[key] = removeEmptyStrings(source[key]);
        }
      } else if (source[key] !== '') {
        finalObject[key] = source[key];
      }

      return finalObject;
    }, {});

  if (_.isNil(settings?.private)) {
    return settings;
  }

  const filteredSettings: IntegrationSettings = _.cloneDeep(settings);
  filteredSettings.private = removeEmptyStrings(filteredSettings.private);
  return filteredSettings;
};

export const flattenWatchErrors = (
  errors: Record<string, any>,
  parentKey: string = ''
) => {
  const ret = {};
  for (const [key, value] of Object.entries(errors)) {
    if (!_.isNil(value.type) && typeof value.type === 'string') {
      ret[`${parentKey}${key}`] = value;
    } else {
      const child = flattenWatchErrors(value, `${parentKey}${key}.`);
      for (const [childKey, childValue] of Object.entries(child)) {
        ret[childKey] = childValue;
      }
    }
  }

  return ret;
};

export const isPrivateObjectEmpty = obj =>
  _.isEmpty(obj) || _.isEqual(obj, { private: { secrets: {} } });

export const isErrorObject = obj => {
  const ERROR_OBJECT_KEYS = ['message', 'ref', 'type'];
  return ERROR_OBJECT_KEYS.reduce(
    (ret, key) => ret && Object.keys(obj).includes(key),
    true
  );
};

export const recursiveParsePrivateErrors = obj => {
  if (isErrorObject(obj)) {
    if (obj.type === 'required') {
      return undefined;
    } else {
      return obj;
    }
  }

  const retObj = {};
  for (const key of Object.keys(obj)) {
    const childObj = recursiveParsePrivateErrors(obj[key]);
    if (childObj) {
      retObj[key] = childObj;
    }
  }

  return retObj;
};

export const parseNonQuickSetupErrors = errors => {
  const finalErrors = _.omit(errors, 'private');

  const privateErrors = _.pick(errors, 'private');
  const privateParsed = recursiveParsePrivateErrors(privateErrors);

  if (!isPrivateObjectEmpty(privateParsed)) {
    finalErrors.private = privateParsed.private;
  }

  return finalErrors;
};

export const recursiveParsePrivateSettings = obj => {
  if (typeof obj !== 'object' || Array.isArray(obj)) {
    if (typeof obj === 'string' && _.isEmpty(obj)) {
      return undefined;
    } else {
      return obj;
    }
  }

  const retObj = {};
  for (const key of Object.keys(obj)) {
    const childObj = recursiveParsePrivateSettings(obj[key]);
    if (childObj) {
      retObj[key] = childObj;
    }
  }

  return retObj;
};

export const parseNonQuickSetupSettings = settings => {
  const finalSettings = _.omit(settings, 'private');

  const privateSettings = _.pick(settings, 'private');
  const privateParsed = recursiveParsePrivateSettings(privateSettings);

  return {
    ...finalSettings,
    ...privateParsed
  };
};

// Convert any empty string to null or return original string
export const convertEmptyStringToNull = string =>
  _.isEmpty(string) ? null : string;
